import React, { useState, KeyboardEvent, useEffect, useCallback } from 'react';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Select } from 'antd';
import { XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { cn } from '@frontend/shadcn/lib/utils';
import { PRODUCT_CONDITION_TYPE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { useGetConnectedShopifyQuery } from '@frontend/app/hooks/useGetConnectedShopifyQuery';
import { useQuery } from '@apollo/client';
import { GET_PRODUCTS_FROM_STORE } from '@frontend/applications/AffiliatesApp/hooks/useGetProductsForStore';
import getDarkHexCodeFromString from '@frontend/app/utils/getDeteministicColor';
import { getCurrencySymbol } from '@frontend/applications/AffiliatesApp/pages/LandingPage/utils/LandingPagePreviewUtils';
import { ClientForSync } from '../../../OfferForm';

interface ProductTag {
  id: string;
  name?: string;
  type?: string;
  imageUrl?: string;
  advertiserId?: number;
}

interface StoreProduct {
  title: string;
  productId: string;
  imageUrl?: string;
  price?: number;
  currency?: string;
}

interface StoreOptionLabel {
  label: string;
  isPrimary: boolean;
  advertiserId: number;
}

const ShopifyProductSelect: React.FC<{
  clientId: string;
  value?: ProductTag[];
  onChange: (products: ProductTag[]) => void;
  className?: string;
  status?: 'error' | '';
  selectedStoreId?: string;
  storeOptions: StoreOptionLabel[];
  onStoreSelect: (storeLabel: string) => void;
  usedStores?: string[];
  loading?: boolean;
}> = ({ clientId, value, onChange, className, status, selectedStoreId, storeOptions, onStoreSelect, usedStores, loading }) => {
  const [productSearch, setProductSearch] = useState('');
  const [allProducts, setAllProducts] = useState<StoreProduct[]>([]);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Get connected Shopify data to map advertiserIds to labels
  const { data: connectedShopifyData } = useGetConnectedShopifyQuery();

  // Create a map of advertiserId to store label
  const advertiserToLabelMap = React.useMemo(() => {
    if (!connectedShopifyData?.clientConnections) return new Map();

    return new Map(
      connectedShopifyData.clientConnections.map((connection) => [
        connection.connectedAdvertiserId.toString(),
        {
          label: connection.label,
          isPrimary: connection.isPrimary
        }
      ])
    );
  }, [connectedShopifyData]);

  // Memoize the selected store to prevent unnecessary recalculations
  const selectedStore = React.useMemo(() => {
    if (selectedStoreId) {
      // If selectedStoreId is a label, find matching store option
      const storeByLabel = storeOptions.find((store) => store.label === selectedStoreId);
      if (storeByLabel) return storeByLabel;

      // If selectedStoreId is an advertiserId, find matching store option using the map
      const storeInfo = advertiserToLabelMap.get(selectedStoreId);
      if (storeInfo) {
        return storeOptions.find((store) => store.label === storeInfo.label);
      }
    }
    return undefined;
  }, [storeOptions, selectedStoreId, advertiserToLabelMap]);

  // Memoize query variables to prevent unnecessary query reruns
  const queryVariables = React.useMemo(
    () => ({
      advertiserId: selectedStore?.advertiserId || null,
      clientId,
      query: productSearch,
      limit: 10,
      endCursor: undefined,
    }),
    [selectedStore?.advertiserId, clientId, productSearch],
  );
  const { data, loading: productsLoading, fetchMore } = useQuery(GET_PRODUCTS_FROM_STORE, {
    variables: queryVariables,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  // Reset products when store changes
  useEffect(() => {
    if (selectedStore?.advertiserId) {
      setAllProducts([]);
      setEndCursor(null);
      setHasReachedEnd(false);
      setProductSearch('');
    }
  }, [selectedStore?.advertiserId]);

  // Update products only when data changes
  useEffect(() => {
    if (!data?.getProductsFromStore?.products) return;

    const newProducts = data.getProductsFromStore.products;
    const pageInfo = data.getProductsFromStore.pageInfo;

    // If it's a fresh search or initial load, replace all products
    if (productSearch || !endCursor) {
      setAllProducts(newProducts);
    } else {
      // For pagination, append new products while avoiding duplicates
      setAllProducts((prevProducts) => {
        const existingIds = new Set(prevProducts.map((p) => p.productId));
        const uniqueNewProducts = newProducts.filter((p) => !existingIds.has(p.productId));
        return [...prevProducts, ...uniqueNewProducts];
      });
    }

    if (pageInfo) {
      setEndCursor(pageInfo.endCursor);
      setHasReachedEnd(!pageInfo.hasNextPage);
    }
  }, [data?.getProductsFromStore, productSearch, endCursor]);

  const handleProductSearch = useCallback(
    async (value: string) => {
      setProductSearch(value);
      setEndCursor(null);
      setAllProducts([]);

      if (!selectedStore?.advertiserId) return;

      try {
        await fetchMore({
          variables: {
            advertiserId: selectedStore.advertiserId,
            clientId,
            query: value,
            limit: 10,
            endCursor: undefined,
          },
          updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
        });
      } catch (error) {
        console.error('Search error:', error);
      }
    },
    [selectedStore?.advertiserId, clientId, fetchMore],
  );

  const handleLoadMore = useCallback(async () => {
    if (
      isLoadingMore ||
      !data?.getProductsFromStore?.pageInfo?.hasNextPage ||
      !endCursor ||
      !selectedStore?.advertiserId
    ) return;

    setIsLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: {
          advertiserId: selectedStore.advertiserId,
          clientId,
          query: productSearch,
          limit: 10,
          endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            getProductsFromStore: {
              ...fetchMoreResult.getProductsFromStore,
              products: [
                ...(prev.getProductsFromStore?.products || []),
                ...fetchMoreResult.getProductsFromStore.products,
              ],
            },
          };
        },
      });

      // Update cursor and hasReachedEnd based on the new result
      if (result.data?.getProductsFromStore?.pageInfo) {
        setEndCursor(result.data.getProductsFromStore.pageInfo.endCursor);
        setHasReachedEnd(!result.data.getProductsFromStore.pageInfo.hasNextPage);
      }
    } catch (error) {
      console.error('Load more error:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    data?.getProductsFromStore?.pageInfo,
    endCursor,
    selectedStore?.advertiserId,
    clientId,
    productSearch,
    fetchMore,
  ]);

  const handleProductChange = useCallback(
    (values: string[]) => {
      const selectedProducts = values.map((productId) => {
        // First try to find the product in allProducts (current session)
        const product = allProducts.find((p) => p.productId === productId);

        // If not found in allProducts, try to find it in the current value (previously selected)
        const existingProduct = value?.find((p) => p.id === productId);

        return {
          id: productId.toString(),
          name: product?.title || existingProduct?.name || productId,
          type: PRODUCT_CONDITION_TYPE.PRODUCT_ID,
          imageUrl: product?.imageUrl || existingProduct?.imageUrl || '',
          advertiserId: selectedStore?.advertiserId,
        };
      });
      onChange(selectedProducts);
    },
    [allProducts, onChange, selectedStore?.advertiserId, value],
  );

  const handleDropdownVisibleChange = useCallback((visible: boolean) => {
    if (!visible) {
      // Reset search and results when dropdown is closed
      setProductSearch('');
      setAllProducts([]);
      setEndCursor(null);
      setHasReachedEnd(false);

      // Force a refetch with empty search
      if (selectedStore?.advertiserId) {
        fetchMore({
          variables: {
            advertiserId: selectedStore.advertiserId,
            clientId,
            query: '',
            limit: 10,
            endCursor: undefined,
          },
          updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
        });
      }
    }
  }, [selectedStore?.advertiserId, clientId, fetchMore]);

  return (
    <div className="flex gap-2 w-full items-center">
      {storeOptions.length > 1 && (
        <div className="w-[30%]">
          <Select
            size="middle"
            placeholder="Select Store"
            style={{ width: '100%' }}
            onChange={onStoreSelect}
            value={selectedStore?.label || selectedStoreId}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ width: 'fit-content' }}
            popupClassName="store-selector-dropdown"
            loading={loading}
          >
            {storeOptions.map((store) => (
              <Select.Option
                key={store.advertiserId}
                value={store.label}
                disabled={usedStores?.includes(store.advertiserId.toString()) &&
                         selectedStoreId !== store.advertiserId.toString()}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: getDarkHexCodeFromString(`${store.label}storeName`)
                    }}
                  />
                  <span className="whitespace-normal">{store.label}</span>
                  {store.isPrimary && (
                    <span className="text-xs text-[#167CF4] whitespace-nowrap">(Primary)</span>
                  )}
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>
      )}
      <div className={cn(storeOptions.length > 1 ? 'w-[70%]' : 'w-full')}>
        <Select
          mode="multiple"
          value={value?.map((v) => v.id)}
          onChange={handleProductChange}
          placeholder="Select Products"
          size="middle"
          style={{ width: '100%' }}
          loading={productsLoading || isLoadingMore || loading}
          onSearch={handleProductSearch}
          filterOption={false}
          showSearch
          disabled={loading || !selectedStoreId}
          className={cn(className, status === 'error' && 'ant-select-error')}
          status={status}
          maxTagTextLength={15}
          listHeight={256}
          dropdownMatchSelectWidth={false}
          optionLabelProp="label"
          dropdownStyle={{ minWidth: '300px', maxWidth: '500px' }}
          tagRender={(props) => {
            const product = value?.find((p) => p.id === props.value);
            return (
              <span className="ant-select-selection-item flex items-center bg-grey-1 rounded-md px-1 py-0.5 h-6 leading-[22px] text-sm">
                <span className="ant-select-selection-item-content">{product?.name || props.value}</span>
                <span
                  className="ant-select-selection-item-remove ml-1 flex items-center h-full px-1"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onClose?.();
                  }}
                >
                  <XmarkIcon className="h-3 w-3 text-grey-4" />
                </span>
              </span>
            );
          }}
          dropdownRender={(menu) => (
            <div>
              {menu}
              {!hasReachedEnd && (
                <div className="flex justify-center py-2 border-t border-grey-2">
                  <Button variant="ghost" size="sm" onClick={handleLoadMore} disabled={isLoadingMore}>
                    {isLoadingMore ? 'Loading...' : 'Load More'}
                  </Button>
                </div>
              )}
            </div>
          )}
          onDropdownVisibleChange={handleDropdownVisibleChange}
          searchValue={productSearch}
        >
          {allProducts.map((product) => (
            <Select.Option
              key={product.productId}
              value={product.productId}
              label={product.title}
            >
              <div className="flex items-center gap-2 max-w-full">
                <img
                  src={product.imageUrl || 'https://storage.googleapis.com/aspireiq-widgets/assets/content_image_placeholder.png'}
                  alt={product.title}
                  className="w-8 h-8 object-cover rounded flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.src = 'https://storage.googleapis.com/aspireiq-widgets/assets/content_image_placeholder.png';
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{product.title}</div>
                  {product.price && <div className="text-sm text-grey-4">{getCurrencySymbol(product.currency)} {product.price}</div>}
                </div>
              </div>
            </Select.Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

// Manual product input component
const ManualProductInput: React.FC<{
  value?: ProductTag[];
  onChange: (products: ProductTag[]) => void;
  className?: string;
  status?: 'error' | '';
}> = ({ value = [], onChange, className, status }) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string>('');

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();

      // Check for duplicate product ID
      const isDuplicate = value.some((product) => product.id === inputValue.trim());

      if (isDuplicate) {
        setDuplicateError('This product ID already exists');
        return;
      }

      const newProduct = {
        id: inputValue.trim(),
        name: inputValue.trim(),
        type: PRODUCT_CONDITION_TYPE.PRODUCT_ID,
      };
      onChange([...value, newProduct]);
      setInputValue('');
      setDuplicateError('');
    }
  };

  const handleRemoveProduct = (productId: string) => {
    const updatedProducts = value.filter((p) => p.id !== productId);
    onChange(updatedProducts);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-1 border rounded-lg bg-white transition-all duration-200',
        isFocused && 'border-primary ring-0 ring-primary',
        (status === 'error' || duplicateError) && 'border-red-500 focus-within:border-red-500 ring-0 ring-red-400',
        className,
      )}
    >
      <div className="flex flex-col w-full">
        <div className="flex items-center gap-1 flex-wrap flex-1 min-h-[20px]">
          {value.map((product) => (
            <div key={product.id} className="flex items-center gap-1 px-2 py-1 bg-grey-1 rounded-md text-sm">
              <span className="text-grey-6">{product.id}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveProduct(product.id);
                }}
              >
                <XmarkIcon className="h-3 w-3 text-grey-4" />
              </Button>
            </div>
          ))}
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setDuplicateError('');
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={value.length === 0 ? 'Enter Product IDs' : ''}
            className={cn(
              'flex-1 min-w-[120px] border-0 focus-visible:ring-0 p-0 h-6',
              (status === 'error' || duplicateError) && 'border-red-500 focus-visible:ring-red-500',
            )}
          />
        </div>
        {duplicateError && <p className="text-red-500 text-xs mt-1">{duplicateError}</p>}
      </div>
    </div>
  );
};

export interface PayoutProductConditionsProps {
  clientId: string;
  isShopifyConnected: boolean;
  value?: ProductTag[];
  onChange: (products: ProductTag[]) => void;
  className?: string;
  status?: 'error' | '';
  selectedStoreId?: string;
  clientsForSync: ClientForSync[];
  usedStores?: string[];
  onStoreSelect: (storeLabel: string) => void;
}

export const PayoutProductConditions: React.FC<PayoutProductConditionsProps> = ({
  clientId,
  isShopifyConnected,
  value,
  onChange,
  className,
  status,
  selectedStoreId: externalSelectedStoreId,
  clientsForSync,
  usedStores,
  onStoreSelect,
}) => {
  const { data: connectedShopifyData, loading: isShopifyDataLoading } = useGetConnectedShopifyQuery();

  // Prepare store options with advertiserIds
  const storeOptions = React.useMemo(() => {
    // Don't make any decisions until we know if we have data or not
    if (isShopifyDataLoading) {
      return [];
    }

    // Now we know for sure if we have connections or not
    if (!connectedShopifyData?.clientConnections?.length || !clientsForSync) {
      return [{
        label: 'Default Store',
        isPrimary: true,
        advertiserId: null,
      }];
    }

    return connectedShopifyData.clientConnections
      .filter((connection) =>
        clientsForSync.some((client) => client.advertiserId === connection.connectedAdvertiserId) ||
        connection.isPrimary
      )
      .map((connection) => ({
        label: connection.label,
        isPrimary: connection.isPrimary,
        advertiserId: connection.connectedAdvertiserId,
      }));
  }, [connectedShopifyData, clientsForSync, isShopifyDataLoading]);

  // Auto-select primary store if it's the only store
  React.useEffect(() => {
    if (storeOptions.length === 1 && !externalSelectedStoreId) {
      onStoreSelect(storeOptions[0].label);
    }
  }, [storeOptions, externalSelectedStoreId, onStoreSelect]);

  if (isShopifyConnected) {
    return (
      <ShopifyProductSelect
        clientId={clientId}
        value={value}
        onChange={onChange}
        className={className}
        status={status}
        selectedStoreId={externalSelectedStoreId || (storeOptions?.length === 1 ? storeOptions[0].label : undefined)}
        storeOptions={storeOptions}
        onStoreSelect={onStoreSelect}
        usedStores={usedStores}
        loading={isShopifyDataLoading}
      />
    );
  }

  return <ManualProductInput value={value} onChange={onChange} className={className} status={status} />;
};
